import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const SHOTS = [
  {
    id: "pro",
    k: "Côté professionnel",
    t: "Votre semaine, tous vos praticiens, un seul écran.",
    d: "La vue agenda regroupe l'ensemble des rendez-vous de l'établissement, praticien par praticien. Les couleurs distinguent les types de prestation, les urgences ressortent en rouge, et la messagerie sécurisée, le dossier et l'annuaire santé restent accessibles depuis le même menu.",
    img: "/screenshots/agenda-pro.jpg",
    w: 1600,
    h: 990,
    alt: "Vue hebdomadaire de l'agenda professionnel, plusieurs praticiens en parallèle",
    pts: [
      "Un agenda par professionnel rattaché, sans supplément",
      "Vue jour, semaine ou établissement",
      "Créneaux d'urgence identifiés d'un coup d'œil",
      "Messagerie MSSanté, consentements et DMP dans la même interface",
    ],
  },
  {
    id: "patient",
    k: "Côté patient",
    t: "Quatre étapes, aucune ligne téléphonique occupée.",
    d: "Depuis votre site vitrine, le patient choisit sa prestation, son praticien et son créneau. Il s'identifie, joint sa pièce complémentaire si nécessaire, puis reçoit sa confirmation. Le rendez-vous apparaît immédiatement dans votre agenda.",
    img: "/screenshots/agenda-patient-rdv.jpg",
    w: 1600,
    h: 862,
    alt: "Module de prise de rendez-vous en ligne, étape du choix de créneau",
    pts: [
      "Prestation, praticien et horaire choisis par le patient",
      "Pièce complémentaire déposée en ligne à l'étape 3",
      "Confirmation immédiate et rappels automatiques",
      "Aucune saisie à reprendre de votre côté",
    ],
  },
  {
    id: "vitrine",
    k: "Votre vitrine",
    t: "Les créneaux sont publiés là où l'on vous cherche.",
    d: "Le site vitrine est inclus et entièrement personnalisable. Il porte votre présentation, vos avis, vos coordonnées et le bouton de prise de rendez-vous — et il travaille votre référencement local pendant que vous travaillez.",
    img: "/screenshots/site-vitrine.jpg",
    w: 1600,
    h: 900,
    alt: "Site vitrine d'un opticien avec bouton de prise de rendez-vous",
    pts: [
      "Contenus, photos et horaires modifiables à tout moment",
      "Bouton de réservation en évidence",
      "Visibilité dans l'annuaire santé",
      "Référencement local travaillé en continu",
    ],
  },
];

const KEYS = [
  { t: "Un agenda par professionnel", d: "Chaque praticien rattaché à l'établissement dispose du sien. Le nombre de professionnels n'a pas d'incidence sur le prix." },
  { t: "Interopérable", d: "L'agenda dialogue avec vos activités et se synchronise avec les solutions déjà en place dans votre établissement." },
  { t: "Rappels automatiques", d: "Le patient est prévenu avant son rendez-vous. Moins d'oublis, moins de créneaux perdus." },
  { t: "Rattaché à votre identité", d: "Le rendez-vous est relié au dossier, à la messagerie sécurisée et aux documents que vous transmettez ensuite." },
];

export default function Agenda() {
  useEffect(() => {
    const h = window.location.hash.slice(1);
    if (!h) return;
    const go = () => {
      const el = document.getElementById(h);
      if (el) el.scrollIntoView({ block: "start" });
    };
    const a = setTimeout(go, 250);
    const b = setTimeout(go, 900);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, []);

  return (
    <div className="mcag">
      <SEO
        title="Agenda et prise de rendez-vous en ligne · MetaConnect"
        description="L'agenda de votre établissement et la prise de rendez-vous en ligne : un agenda par professionnel, créneaux publiés sur votre site, rappels automatiques."
        canonical="/agenda"
      />
      <style>{CSS}</style>

      <section className="mcag-hero">
        <div className="mcag-wrap">
          <span className="mcag-pill">Agenda &amp; prise de rendez-vous</span>
          <h1>
            L'agenda de votre établissement,
            <br />
            et la prise de rendez-vous en ligne.
          </h1>
          <p className="mcag-lede">
            Un agenda par professionnel, des créneaux publiés sur votre site vitrine, des rappels
            envoyés tout seuls. Le patient réserve sans vous appeler, et vous ouvrez votre journée
            déjà organisée.
          </p>
          <div className="mcag-actions">
            <Link className="mcag-btn mcag-btn-primary" to="/demo">
              Prendre rendez-vous
            </Link>
            <Link className="mcag-btn mcag-btn-ghost" to="/plateforme">
              Voir la plateforme
            </Link>
          </div>
        </div>
      </section>

      {SHOTS.map((s, i) => (
        <section className={"mcag-sec" + (i % 2 === 1 ? " mcag-alt" : "")} id={s.id} key={s.img}>
          <div className="mcag-wrap">
            <div className="mcag-head">
              <span className="mcag-k">{s.k}</span>
              <h2>{s.t}</h2>
              <p>{s.d}</p>
            </div>
            <figure className="mcag-frame">
              <div className="mcag-bar">
                <i />
                <i />
                <i />
              </div>
              <img src={s.img} alt={s.alt} width={s.w} height={s.h} />
            </figure>
            <ul className="mcag-pts">
              {s.pts.map((p) => (
                <li key={p}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      <section className="mcag-sec mcag-alt">
        <div className="mcag-wrap">
          <div className="mcag-head">
            <span className="mcag-k">À retenir</span>
            <h2>Ce que l'agenda change, concrètement.</h2>
          </div>
          <div className="mcag-keys">
            {KEYS.map((k) => (
              <div className="mcag-key" key={k.t}>
                <h3>{k.t}</h3>
                <p>{k.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mcag-cta">
        <div className="mcag-wrap">
          <div className="mcag-k mcag-k-light">Offre</div>
          <h2>
            L'agenda est inclus dans l'offre Messagerie + Agenda,
            <br />à 49,99 € HT par mois et par établissement.
          </h2>
          <p>
            Tous vos professionnels sont rattachés, sans supplément. Une question sur le
            fonctionnement, la reprise de vos créneaux ou la synchronisation ? Prenez un rendez-vous,
            on vous montre l'outil en direct.
          </p>
          <Link className="mcag-btn mcag-btn-white" to="/demo">
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </div>
  );
}

const CSS = `
.mcag{
  --ground:#F5F5F5;--surface:#fff;--line:#E6E8EC;--text:#0F172A;--muted:#5A6478;
  --blue:#0080FF;--blue700:#0069D1;--ink:#0F172A;
  background:var(--ground);color:var(--text);
  font-family:"Instrument Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  -webkit-font-smoothing:antialiased;
}
.mcag *{box-sizing:border-box}
.mcag img{max-width:100%;display:block}
.mcag-frame img{width:100%;height:auto}
.mcag h1,.mcag h2,.mcag h3{margin:0;color:var(--text);font-weight:600;text-wrap:balance}
.mcag h1{font-size:clamp(30px,4vw,44px);line-height:1.13;letter-spacing:-.02em}
.mcag h2{font-size:clamp(22px,2.4vw,30px);line-height:1.2;letter-spacing:-.015em}
.mcag h3{font-size:16.5px;line-height:1.35}
.mcag p{margin:0;color:var(--muted);line-height:1.6}
.mcag ul{list-style:none;margin:0;padding:0}
.mcag-wrap{max-width:1080px;margin:0 auto;padding:0 28px}

.mcag-pill{display:inline-flex;border-radius:999px;background:#E7ECF3;color:#45526B;
  font-size:11.5px;font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.mcag-k{display:block;font-size:11.5px;font-weight:700;letter-spacing:.14em;
  text-transform:uppercase;color:var(--blue);margin-bottom:12px}
.mcag-k-light{color:rgba(255,255,255,.75)}

.mcag-btn{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;
  font-weight:600;font-size:15px;text-decoration:none;border:1px solid transparent;
  padding:13px 26px;transition:background .15s,border-color .15s}
.mcag-btn-primary{background:var(--blue);color:#fff}
.mcag-btn-primary:hover{background:var(--blue700);color:#fff}
.mcag-btn-ghost{border-color:var(--line);color:var(--text);background:var(--surface)}
.mcag-btn-ghost:hover{border-color:var(--blue);color:var(--text)}
.mcag-btn-white{background:#fff;color:var(--blue)}
.mcag-btn-white:hover{background:#EAF4FF;color:var(--blue)}

.mcag-hero{padding:88px 0 56px;text-align:center}
.mcag-hero h1{margin:18px auto 16px;max-width:22ch}
.mcag .mcag-lede{max-width:62ch;margin:0 auto;font-size:17px}
.mcag-actions{display:flex;justify-content:center;flex-wrap:wrap;gap:12px;margin-top:28px}

.mcag-sec{padding:58px 0;border-top:1px solid var(--line);scroll-margin-top:84px}
.mcag-alt{background:var(--surface)}
.mcag-head{max-width:64ch;margin:0 auto 30px;text-align:center}
.mcag-head h2{margin-bottom:12px}
.mcag-head p{font-size:15.5px}

.mcag-frame{margin:0;border-radius:16px;overflow:hidden;background:#fff;
  border:1px solid var(--line);box-shadow:0 26px 60px -34px rgba(15,23,42,.45)}
.mcag-bar{display:flex;align-items:center;gap:7px;padding:11px 14px;
  background:#F2F4F7;border-bottom:1px solid var(--line)}
.mcag-bar i{width:9px;height:9px;border-radius:50%;background:#D3D8E0}

.mcag .mcag-pts{display:grid;grid-template-columns:repeat(2,1fr);gap:12px 26px;margin-top:26px;
  max-width:860px;margin-left:auto;margin-right:auto}
.mcag-pts li{display:flex;gap:11px;font-size:14.5px;line-height:1.5;color:#3D4759}
.mcag-pts svg{width:15px;height:15px;flex:0 0 15px;margin-top:4px;stroke:#16A34A;fill:none;
  stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}

.mcag-keys{display:grid;grid-template-columns:repeat(2,1fr);gap:16px}
.mcag-key{background:var(--ground);border:1px solid var(--line);border-radius:16px;padding:24px 22px}
.mcag-key h3{margin-bottom:8px}
.mcag-key p{font-size:14.2px}

.mcag-cta{background:var(--blue);padding:60px 0;text-align:center}
.mcag-cta h2{color:#fff;font-size:clamp(23px,2.7vw,32px);margin-bottom:14px}
.mcag-cta p{color:rgba(255,255,255,.9);font-size:15.5px;max-width:64ch;margin:0 auto 26px}

@media (max-width:820px){
  .mcag-pts,.mcag-keys{grid-template-columns:1fr}
  .mcag-hero{padding:48px 0 40px}
}
.mcag a:focus-visible{outline:2px solid var(--blue);outline-offset:3px;border-radius:6px}
`;
